import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import DropdownInput from 'components/DropdownInput';
import LoadingSpinner from 'components/LoadingSpinner';
import { makeStyles } from '@material-ui/core';
import useApi, { GET_OVERLAYS } from 'hooks/useApi';
import useFetchDashboardMasterObject from 'hooks/useFetchDashboardMasterObject';
import TimeSeriesSetIdSelector from 'components/Widget/components/menus/MainSettings/components/TimeSeriesSetIdSelector';
import BenchmarkSection from './components/BenchmarkSection';
import MasterObjectSection from './components/MasterObjectSection';
import { actions } from '../../config';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  input: {
    '&:hover': {
      color: theme.palette.primary.main
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        color: theme.palette.primary.main,
        borderColor: theme.palette.accent.main,
        borderWidth: '2px'
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      }
    }
  },
  divider: {
    width: '100%'
  },
  heading: {
    marginBottom: '10px',
    marginTop: '0',
    opacity: '0.7'
  }
}));

const FirstStep = ({
  setActiveStep,
  dispatchWidget,
  widgetState,
  baseWidgetId,
  setWhichPopup,
  dashboardData
}) => {
  const classes = useStyles();
  const {
    widgetOverlayId,
    overlayDropdown,
    title,
    masterObjectTypeId,
    masterObjectId
  } = widgetState;

  const [
    masterObjectTypeIdLookupState,
    masterObjects,
    dispatchMasterObject
  ] = useFetchDashboardMasterObject({
    masterObjectTypeId
  });

  const { isLoading } = masterObjectTypeIdLookupState;
  const [overlayState, getOverlays] = useApi();
  const { data: overlayResponse, isLoading: isOverlayLoading } = overlayState;
  const { masterObjectTimeSeriesSetId } = widgetState;

  const handleFormStateChange = event => {
    const targetName = event.target.name;

    dispatchWidget({
      type: actions.UPDATED_WIDGET,
      payload: { [targetName]: event.target.value }
    });
  };

  const handleBack = () => setWhichPopup('widget_preview');

  const handleNextStep = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleTimeSeriesSetIdChange = selected => {
    dispatchWidget({
      type: actions.UPDATED_WIDGET,
      payload: { timeSeriesSetId: selected }
    });
  };

  useEffect(() => {
    getOverlays(GET_OVERLAYS({ baseWidgetId, objectType: masterObjectTypeId }));
  }, [masterObjectTypeId, baseWidgetId, getOverlays]);

  useEffect(() => {
    if (masterObjectTimeSeriesSetId) {
      handleTimeSeriesSetIdChange(masterObjectTimeSeriesSetId);
    }
  }, [masterObjectTimeSeriesSetId]);

  useEffect(() => {
    function transformOverlays(overlays) {
      const { data } = overlays;
      return [...data].map(overlay => ({
        label: overlay.name,
        value: overlay.id
      }));
    }

    if (overlayResponse) {
      dispatchWidget({
        type: actions.UPDATED_WIDGET,
        payload: { overlayDropdown: transformOverlays(overlayResponse) }
      });
    }
  }, [dispatchWidget, overlayResponse]);

  return (
    <>
      <Grid container direction="row" justify="flex-start" alignItems="flex-end" spacing={2}>
        <Grid item xs={12} md={6}>
          <h3 className={classes.heading}>Widget Name</h3>
          <TextField
            onChange={handleFormStateChange}
            value={title}
            name="title"
            label="Display name"
            variant="outlined"
            size="small"
            fullWidth
            classes={{
              root: classes.input
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <h3 className={classes.heading}>Widget Overlay *</h3>
          {isOverlayLoading ? (
            <LoadingSpinner />
          ) : (
            <DropdownInput
              selected={widgetOverlayId}
              options={overlayDropdown}
              placeholder="Choose..."
              onSelect={handleFormStateChange}
              label="Widget Overlay"
              name="widgetOverlayId"
            />
          )}
        </Grid>
        <Box display="flex" my={2} px={1} justifyContent="center" width="100%">
          <Divider className={classes.divider} />
        </Box>

        <MasterObjectSection
          dispatchUpdate={dispatchWidget}
          updateAction={actions.UPDATED_WIDGET}
          objectState={widgetState}
          masterObjectTypeIdLookupState={masterObjectTypeIdLookupState}
          masterObjects={masterObjects}
          dispatchMasterObject={dispatchMasterObject}
        />

        <Box display="flex" my={2} px={1} justifyContent="center" width="100%">
          <Divider className={classes.divider} />
        </Box>

        <Grid item xs={12} md={6}>
          <h3 className={classes.heading}>Peer Group</h3>
          <TimeSeriesSetIdSelector
            handleSelect={handleTimeSeriesSetIdChange}
            dashboardData={widgetState}
            timeSeriesSetId={dashboardData.timeSeriesSetId}
          />
        </Grid>

        <Box width="100%" my={3} mx={1}>
          <Divider style={{ width: '100%' }} />
        </Box>

        <BenchmarkSection
          objectState={widgetState}
          dispatchUpdate={dispatchWidget}
          isLoading={isLoading}
          actions={actions}
        />
      </Grid>

      <Divider className={classes.divider} style={{ marginTop: '20px' }} />

      <Grid container direction="row" justify="space-between" alignItems="center" spacing={2}>
        <Grid item xs={4} md={4}>
          <Box display="flex" my={2} justifyContent="flex-start" mr="auto">
            <Button
              variant="outlined"
              style={{ marginRight: '8px' }}
              color="primary"
              onClick={handleBack}
            >
              Back
            </Button>
          </Box>
        </Grid>

        <Grid item xs={4} md={4}>
          <Box display="flex" my={2} justifyContent="flex-end" ml="auto">
            <Button
              variant="contained"
              color="primary"
              onClick={handleNextStep}
              disabled={!(widgetOverlayId && (dashboardData.isCanned || masterObjectId))}
            >
              Next
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

FirstStep.propTypes = {
  dashboardData: PropTypes.shape({
    isCanned: PropTypes.bool,
    timeSeriesSetId: PropTypes.number
  }),
  widgetState: PropTypes.shape({
    title: PropTypes.string,
    name: PropTypes.string,
    emailAddress: PropTypes.string,
    dateRangeType: PropTypes.number,
    firstName: PropTypes.string,
    widgetOverlayId: PropTypes.string,
    lastName: PropTypes.string,
    telephoneNumber: PropTypes.number,
    shouldInheritBenchmarks: PropTypes.bool,
    isAdvancedSearch: PropTypes.bool,
    isBenchmarkAdvancedSearch: PropTypes.string,
    masterObjectId: PropTypes.number,
    masterObjectTypeId: PropTypes.number,
    endReportingDate: PropTypes.string,
    slots: PropTypes.string,
    startReportingDate: PropTypes.string,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        benchmarkId: PropTypes.number,
        name: PropTypes.string
      })
    ),
    overlayDropdown: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.number,
        value: PropTypes.string
      })
    )
  }),
  dispatchWidget: PropTypes.func,
  setActiveStep: PropTypes.func,
  baseWidgetId: PropTypes.string.isRequired,
  setWhichPopup: PropTypes.func.isRequired,
  masterObjectTypeIdLookupState: PropTypes.shape({
    isLoading: PropTypes.bool
  }).isRequired
};

FirstStep.defaultProps = {
  dashboardData: {
    isCanned: false,
    timeSeriesSetId: null
  },
  widgetState: {},
  dispatchWidget: null,
  setActiveStep: null
};
export default FirstStep;
